import type {
	Installation,
	Location,
	Ref,
	Sensor,
	Zone,
} from "@sitescene/types";

import { SensorStatus } from "@sitescene/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Heading } from "react-aria-components";
import { Link } from "react-router";

import { queryKeys } from "@/api/query-keys";
import { LoadingIndicator } from "@/components/loading-indicator/loading-indicator";
import { Dialog, Modal, ModalOverlay } from "@/components/modals/modal";
import { StatusMessage } from "@/components/status-messages/status-message";
import { Badge } from "@/components/ui/badges/badges";
import { Button } from "@/components/ui/buttons/button";
import { CloseButton } from "@/components/ui/buttons/close-button";
import { textLinkStyles } from "@/components/ui/links/text-link-styles";
import { ROUTES } from "@/constants/routes";
import { useOrgLocationTree } from "@/hooks/useOrgLocationTree";
import { useGetSensors } from "@/hooks/useSensors";
import posthog from "@/lib/posthog";
import { cx } from "@/utils/cx";
import {
	getSensorDurationLabel,
	type SensorDurationLabel,
	sensorStatusConfig,
} from "@/utils/sensors";
import { toTitleCase } from "@/utils/strings";

interface SensorMetadata {
	locationId?: string;
	locationName?: string;
	zoneId?: string;
	zoneName?: string;
}

interface SensorsStatusModalProps {
	defaultFilter?: null | SensorStatus;
	isOpen: boolean;
	offlineCount?: number;
	onClose: () => void;
	onSelectedStatusChange: (status: null | SensorStatus) => void;
	selectedStatus: null | SensorStatus;
	source: "offline_banner" | "sidebar";
}

interface SensorWithDisplay extends Sensor {
	displayLocationId?: string;
	displayLocationName: string;
	displayZoneId?: string;
	displayZoneName: string;
	durationLabel: null | SensorDurationLabel;
	statusBadgeColor: ReturnType<typeof sensorStatusConfig>["color"];
	statusBadgeLabel: string;
}

const statusFilterConfig: Array<{
	filter: SensorStatus;
	label: string;
}> = [
	{ filter: SensorStatus.Active, label: "Online" },
	{ filter: SensorStatus.Warning, label: "Warnings" },
	{ filter: SensorStatus.Offline, label: "Offline" },
];

const getRefName = <T extends { name: string }>(
	ref?: Ref<T>,
): string | undefined => {
	if (!ref) return undefined;
	return typeof ref === "string" ? undefined : ref.name;
};

const sensorStatusFilterLabel = (
	status: null | SensorStatus,
): "active" | "all" | "offline" | "warning" => {
	if (status === SensorStatus.Active) return "active";
	if (status === SensorStatus.Warning) return "warning";
	if (status === SensorStatus.Offline) return "offline";
	return "all";
};

export const SensorsStatusModal = ({
	defaultFilter = null,
	isOpen,
	offlineCount,
	onClose,
	onSelectedStatusChange,
	selectedStatus,
	source,
}: SensorsStatusModalProps) => {
	const queryClient = useQueryClient();
	const { locations } = useOrgLocationTree();
	const {
		isLoadingSensors,
		isSensorsError,
		sensorsData = [],
	} = useGetSensors({ enabled: isOpen });

	// Card and modal read different queries (`sensors.summary` vs `sensors.lists`)
	// which can drift between refetches. Invalidate both when the modal opens so
	// the counts the user clicked on and the detail rows shown here come from
	// the same snapshot.
	useEffect(() => {
		if (!isOpen) return;
		queryClient.invalidateQueries({ queryKey: queryKeys.sensors.summary() });
		queryClient.invalidateQueries({ queryKey: queryKeys.sensors.lists() });
	}, [isOpen, queryClient]);

	useEffect(() => {
		if (!isOpen) return;
		posthog.capture("sensor_status_modal_opened", {
			default_filter: sensorStatusFilterLabel(defaultFilter),
			offline_count: offlineCount ?? 0,
			source,
		});
		// Capture once per open — omit offlineCount so summary refetches don't
		// look like additional opens.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- see above
	}, [isOpen]);

	const metadataBySensorId = useMemo(() => {
		const metadata = new Map<string, SensorMetadata>();

		for (const location of locations) {
			const zonesById = new Map<string, string>(
				(location.zones ?? []).map((zone) => [zone.id, zone.name]),
			);
			for (const sensor of location.sensors ?? []) {
				const zoneNameFromRef = getRefName(
					sensor.zone as Ref<Zone> | undefined,
				);
				const zoneIdFromRef =
					typeof sensor.zone === "string" ? sensor.zone : sensor.zone?.id;
				const zoneName = zoneNameFromRef ?? zonesById.get(zoneIdFromRef ?? "");

				metadata.set(sensor.id, {
					locationId: location.id,
					locationName: location.name,
					zoneId: zoneIdFromRef,
					zoneName,
				});
			}
		}

		return metadata;
	}, [locations]);

	const statusCounts = useMemo(() => {
		return {
			[SensorStatus.Active]: sensorsData.filter(
				(sensor) => sensor.status === SensorStatus.Active,
			).length,
			[SensorStatus.Offline]: sensorsData.filter(
				(sensor) => sensor.status === SensorStatus.Offline,
			).length,
			[SensorStatus.Warning]: sensorsData.filter(
				(sensor) => sensor.status === SensorStatus.Warning,
			).length,
		};
	}, [sensorsData]);

	const sensorsWithDisplay = useMemo<SensorWithDisplay[]>(() => {
		return sensorsData
			.map((sensor) => {
				const metadata = metadataBySensorId.get(sensor.id);
				const installationLocationRef =
					typeof sensor.installation === "string"
						? undefined
						: (sensor.installation as Installation).location;
				const displayLocationName =
					getRefName(installationLocationRef as Ref<Location> | undefined) ??
					metadata?.locationName ??
					"Unknown";
				const displayZoneName =
					getRefName(sensor.zone as Ref<Zone> | undefined) ??
					metadata?.zoneName ??
					"Unassigned";
				const referenceTimestamp = sensor.lastInputReceived ?? sensor.updatedAt;
				const durationLabel = getSensorDurationLabel(
					sensor.status,
					referenceTimestamp,
				);
				const { color: statusBadgeColor, label: statusBadgeLabel } =
					sensorStatusConfig(sensor.status);

				return {
					...sensor,
					displayLocationId: metadata?.locationId,
					displayLocationName,
					displayZoneId:
						metadata?.zoneId ??
						(typeof sensor.zone === "string" ? sensor.zone : sensor.zone?.id),
					displayZoneName,
					durationLabel,
					statusBadgeColor,
					statusBadgeLabel,
				};
			})
			.sort((a, b) =>
				a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
			);
	}, [metadataBySensorId, sensorsData]);

	const filteredSensors = useMemo(() => {
		if (selectedStatus === null) {
			return sensorsWithDisplay;
		}
		return sensorsWithDisplay.filter(
			(sensor) => sensor.status === selectedStatus,
		);
	}, [selectedStatus, sensorsWithDisplay]);

	return (
		<ModalOverlay
			isDismissable
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					onClose();
				}
			}}
		>
			<Modal isDismissable>
				<Dialog>
					<div className="pointer-events-auto relative flex h-screen w-full flex-col overflow-hidden bg-primary shadow-xl sm:h-auto sm:max-h-[85vh] sm:rounded-2xl md:max-w-lg">
						<div className="flex items-center justify-between border-b border-secondary px-6 py-5">
							<Heading
								className="text-md font-semibold text-primary"
								slot="title"
							>
								Sensors Status
							</Heading>
							<CloseButton
								className="absolute top-3 right-3 z-20"
								onClick={onClose}
								size="lg"
								theme="light"
							/>
						</div>

						<div className="mx-6 flex flex-col border-b border-secondary py-4">
							<p className="my-2 text-md text-secondary">
								Please{" "}
								<Link
									className={textLinkStyles.primary}
									onClick={onClose}
									to={ROUTES.SUPPORT}
								>
									contact support
								</Link>{" "}
								if any sensors require attention or if you have any questions.
							</p>

							<div className="grid grid-cols-3 gap-1 rounded-xl bg-primary">
								{statusFilterConfig.map(({ filter, label }) => {
									const isSelected = selectedStatus === filter;
									return (
										<button
											className={cx(
												"flex cursor-pointer items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm text-xs font-medium font-semibold transition-colors",
												isSelected
													? "bg-utility-brand-50 text-utility-brand-700 ring-utility-brand-200"
													: "text-tertiary hover:text-secondary",
											)}
											key={filter}
											onClick={() => {
												const nextStatus =
													selectedStatus === filter ? null : filter;
												posthog.capture("sensor_status_filter_changed", {
													new_filter: sensorStatusFilterLabel(nextStatus),
													previous_filter:
														sensorStatusFilterLabel(selectedStatus),
													source,
												});
												onSelectedStatusChange(nextStatus);
											}}
											type="button"
										>
											<span>{label}</span>
											<Badge
												className={cx(
													isSelected
														? "bg-utility-brand-50 text-utility-brand-700 ring-utility-brand-200"
														: "text-tertiary hover:text-secondary",
												)}
												color="gray"
												size="sm"
												type="pill-color"
											>
												{statusCounts[filter]}
											</Badge>
										</button>
									);
								})}
							</div>
						</div>

						<div className="scrollbar-hide flex flex-1 flex-col overflow-y-auto px-6 pb-6">
							{isLoadingSensors ? (
								<div className="py-10">
									<LoadingIndicator className="mx-auto" size="md" />
								</div>
							) : isSensorsError ? (
								<StatusMessage
									description="Failed to load sensors."
									type="error"
								/>
							) : filteredSensors.length === 0 ? (
								<StatusMessage
									description={
										selectedStatus === null
											? "No sensors found."
											: `No ${sensorStatusConfig(selectedStatus).label.toLowerCase()} sensors found.`
									}
									title="No sensors to display"
									type="info"
								/>
							) : (
								<div className="divide-secondary border border-r-0 border-l-0 border-secondary first:border-0">
									{filteredSensors.map((sensor) => {
										const locationHref = sensor.displayLocationId
											? `${ROUTES.LOCATION_MANAGEMENT}/${sensor.displayLocationId}?tab=sensors&id=${sensor.id}`
											: undefined;
										return (
											<div
												className="flex items-center justify-between gap-4 border-b px-5 py-4 text-sm last:border-b-0"
												key={sensor.id}
											>
												<div>
													{locationHref ? (
														<Link
															className="font-semibold text-primary hover:underline"
															onClick={() => {
																posthog.capture(
																	"sensor_clicked_from_status_modal",
																	{
																		location_name: sensor.displayLocationName,
																		sensor_id: sensor.id,
																		sensor_name: sensor.name,
																		source,
																		status: sensor.status,
																		zone_name: sensor.displayZoneName,
																		...(sensor.displayLocationId && {
																			location_id: sensor.displayLocationId,
																		}),
																		...(sensor.displayZoneId && {
																			zone_id: sensor.displayZoneId,
																		}),
																	},
																);
																onClose();
															}}
															to={locationHref}
														>
															{toTitleCase(sensor.name)}
														</Link>
													) : (
														<p className="font-semibold text-primary">
															{toTitleCase(sensor.name)}
														</p>
													)}
													<p className="mt-1 text-sm text-tertiary">
														Type: {toTitleCase(sensor.type)}
													</p>
													<p className="text-sm text-tertiary">
														Location: {toTitleCase(sensor.displayLocationName)}
													</p>
													<p className="text-sm text-tertiary">
														Zone: {toTitleCase(sensor.displayZoneName)}
													</p>
													{sensor.durationLabel && (
														<p
															className={cx(
																"mt-1 text-sm font-medium",
																sensor.durationLabel.color,
															)}
														>
															{sensor.durationLabel.text}
														</p>
													)}
												</div>
												<Badge
													color={sensor.statusBadgeColor}
													size="md"
													type="color"
												>
													{sensor.statusBadgeLabel}
												</Badge>
											</div>
										);
									})}
								</div>
							)}
						</div>

						<div className="flex justify-end border-t border-secondary px-6 py-4">
							<Button color="secondary" onClick={onClose} size="md">
								Close
							</Button>
						</div>
					</div>
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
};
